import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import Seo from '../components/Seo';
import { Building2 } from 'lucide-react';
import { AlertTriangle } from 'lucide-react';
import { CheckCircle2 } from 'lucide-react';
import { Info } from 'lucide-react';
import { ArrowRight } from 'lucide-react';
import { trackQuoteFormSubmit } from '../lib/analytics';

const RequestPatientTransportation = () => {
  const [status, setStatus] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [oxygenRequired, setOxygenRequired] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setStatus('');
    const fd = new FormData(e.target);
    const payload = {
      requesting_organization: fd.get('org'),
      requestor_name: fd.get('requestorName'),
      requestor_title: fd.get('requestorTitle') || null,
      phone: fd.get('phone'),
      email: fd.get('email'),
      patient_name: fd.get('patientName'),
      pickup_address: fd.get('pickup'),
      destination: fd.get('destination'),
      appointment_date: fd.get('apptDate'),
      appointment_time: fd.get('apptTime'),
      transport_type: fd.get('transportType'),
      trip_type: fd.get('tripType'),
      oxygen_required: fd.get('oxygen') === 'yes',
      claim_authorization: fd.get('claimNumber') || null,
      special_instructions: fd.get('instructions') || null,
    };
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/inquiries/patient-transport`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error();
      trackQuoteFormSubmit({
        source_page: 'request_patient_transportation',
        transport_type: payload.transport_type || 'unknown',
        trip_type: payload.trip_type || 'unknown',
      });
      setStatus('success');
      e.target.reset();
      setOxygenRequired(false);
    } catch (err) {
      setStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-20" data-testid="patient-transport-page">
      <Seo
        title="Request Patient Transportation | Healthcare Referral Intake | MoveraCare"
        description="For case managers, adjusters, TPA coordinators, hospitals, SNFs, and healthcare professionals: submit a non-emergency medical transportation request for a patient across Greater Houston."
        path="/request-patient-transportation"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Request Patient Transportation', url: '/request-patient-transportation' },
        ]}
      />
      {/* Hero */}
      <section className="py-14 bg-[#004C97]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center gap-2 bg-[#F9A825] text-[#004C97] px-4 py-1.5 rounded-full text-sm font-bold mb-5">
            <Building2 className="w-4 h-4" />
            <span>FOR HEALTHCARE PROFESSIONALS</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 leading-tight">
            Request Transportation for a Patient
          </h1>
          <p className="text-lg text-gray-200 leading-relaxed">
            For case managers, adjusters, TPA coordinators, hospitals, SNFs, dialysis centers, and
            other healthcare professionals coordinating non-emergency transportation.
          </p>
        </div>
      </section>

      {/* Form */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-2 border-gray-100">
            <CardContent className="p-6 md:p-8">
              <form onSubmit={submit} className="space-y-5" data-testid="patient-transport-form">
                <h2 className="text-xl font-bold text-[#004C97] border-b pb-2">Requesting Party</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><label className="block text-sm font-semibold text-[#004C97] mb-1">Requesting Organization *</label><input required name="org" className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:border-[#F9A825] focus:outline-none" /></div>
                  <div><label className="block text-sm font-semibold text-[#004C97] mb-1">Requestor Name *</label><input required name="requestorName" className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:border-[#F9A825] focus:outline-none" /></div>
                  <div><label className="block text-sm font-semibold text-[#004C97] mb-1">Title / Role</label><input name="requestorTitle" placeholder="Case Manager, Adjuster…" className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:border-[#F9A825] focus:outline-none" /></div>
                  <div><label className="block text-sm font-semibold text-[#004C97] mb-1">Phone *</label><input required name="phone" className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:border-[#F9A825] focus:outline-none" /></div>
                  <div className="md:col-span-2"><label className="block text-sm font-semibold text-[#004C97] mb-1">Email *</label><input required type="email" name="email" className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:border-[#F9A825] focus:outline-none" /></div>
                </div>

                <h2 className="text-xl font-bold text-[#004C97] border-b pb-2 pt-2">Patient &amp; Trip</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2"><label className="block text-sm font-semibold text-[#004C97] mb-1">Patient Name *</label><input required name="patientName" className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:border-[#F9A825] focus:outline-none" /></div>
                  <div className="md:col-span-2"><label className="block text-sm font-semibold text-[#004C97] mb-1">Pickup Address *</label><input required name="pickup" className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:border-[#F9A825] focus:outline-none" /></div>
                  <div className="md:col-span-2"><label className="block text-sm font-semibold text-[#004C97] mb-1">Destination *</label><input required name="destination" className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:border-[#F9A825] focus:outline-none" /></div>
                  <div><label className="block text-sm font-semibold text-[#004C97] mb-1">Appointment Date *</label><input required type="date" name="apptDate" className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:border-[#F9A825] focus:outline-none" /></div>
                  <div><label className="block text-sm font-semibold text-[#004C97] mb-1">Appointment Time *</label><input required type="time" name="apptTime" className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:border-[#F9A825] focus:outline-none" /></div>
                  <div><label className="block text-sm font-semibold text-[#004C97] mb-1">Transportation Type *</label>
                    <select required name="transportType" className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:border-[#F9A825] focus:outline-none">
                      <option value="ambulatory">Ambulatory</option>
                      <option value="wheelchair">Wheelchair</option>
                    </select>
                  </div>
                  <div><label className="block text-sm font-semibold text-[#004C97] mb-1">Trip Type *</label>
                    <select required name="tripType" className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:border-[#F9A825] focus:outline-none">
                      <option value="one-way">One Way</option>
                      <option value="round-trip">Round Trip</option>
                      <option value="wait-and-return">Wait and Return</option>
                      <option value="recurring">Recurring</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#004C97] mb-2">Portable Oxygen Required?</label>
                  <div className="flex gap-4">
                    <label className="inline-flex items-center gap-2"><input type="radio" name="oxygen" value="no" defaultChecked onChange={() => setOxygenRequired(false)} /> <span>No</span></label>
                    <label className="inline-flex items-center gap-2"><input type="radio" name="oxygen" value="yes" onChange={() => setOxygenRequired(true)} /> <span>Yes</span></label>
                  </div>
                  {oxygenRequired && (
                    <div className="mt-3 bg-[#F9A825]/10 border-l-4 border-[#F9A825] rounded p-3 flex items-start gap-2">
                      <Info className="w-4 h-4 text-[#F9A825] mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-gray-700">
                        Oxygen requirements are subject to MoveraCare&apos;s non-emergency transportation
                        policies and passenger eligibility. Dispatch will confirm vehicle suitability
                        and requirements before scheduling.
                      </p>
                    </div>
                  )}
                </div>

                <h2 className="text-xl font-bold text-[#004C97] border-b pb-2 pt-2">Authorization</h2>
                <div>
                  <label className="block text-sm font-semibold text-[#004C97] mb-1">Claim / Authorization / Reference Number</label>
                  <input name="claimNumber" placeholder="Workers' comp claim, PO number, referral ID…" className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:border-[#F9A825] focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#004C97] mb-1">Special Transportation Instructions</label>
                  <textarea name="instructions" rows="3" placeholder="Any mobility, access, communication, or scheduling notes." className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:border-[#F9A825] focus:outline-none"></textarea>
                </div>

                <Button type="submit" disabled={submitting} size="lg" className="bg-[#F9A825] text-[#004C97] hover:bg-[#E08E1E] font-bold w-full" data-testid="submit-patient-transport">
                  {submitting ? 'Submitting…' : 'Submit Transportation Request'}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>

                {status === 'success' && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-green-800 text-sm flex items-start gap-2" data-testid="form-success">
                    <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
                    <span>Your transportation request has been received. MoveraCare Dispatch will review and confirm availability.</span>
                  </div>
                )}
                {status === 'error' && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800 text-sm flex items-start gap-2" data-testid="form-error">
                    <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                    <span>Submission failed. Please call Dispatch at 832-925-2128 or email moveracareservices@gmail.com.</span>
                  </div>
                )}

                <div className="bg-gray-100 border-l-4 border-gray-400 rounded p-3 flex items-start gap-2 mt-4">
                  <Info className="w-4 h-4 text-gray-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-gray-700 leading-relaxed">
                    Submitting this request does not confirm transportation. MoveraCare Dispatch will
                    review the request and confirm availability.
                  </p>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default RequestPatientTransportation;
